import {
  CallQueueRow,
  DashboardSectionAction,
  DashboardSectionTitle,
  CallsListSkeleton,
  Card,
  CardContent,
  navigateWithThreadPreservation,
} from '@yeshuman/sdk'
import type { CallRecordListItem } from '@yeshuman/sdk'
import { useNavigate, useSearchParams } from 'react-router-dom'


export type CallAttentionModuleProps = {
  attentionCalls: CallRecordListItem[]
  evaluationBacklogCount: number
  loading?: boolean
}

export function CallAttentionModule({
  attentionCalls,
  evaluationBacklogCount,
  loading = false,
}: CallAttentionModuleProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const urgentLive = attentionCalls.filter((r) => {
    const s = (r.call_state || '').toLowerCase()
    return !r.is_complete && (s === 'emergency' || s === 'elevated')
  })

  return (
    <section className="space-y-4" aria-labelledby="call-attention-heading">
      <DashboardSectionTitle
        id="call-attention-heading"
        title="Needs attention"
        action={
          <DashboardSectionAction
            onClick={() =>
              navigateWithThreadPreservation(
                navigate,
                '/calls/records',
                searchParams
              )
            }
          >
            Live board
          </DashboardSectionAction>
        }
      />

      {loading ? (
        <CallsListSkeleton count={2} ariaLabel="Loading calls needing attention" />
      ) : urgentLive.length > 0 || attentionCalls.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 @3col:grid-cols-2">
          {attentionCalls.slice(0, 4).map((row) => (
            <CallQueueRow key={row.id} variant="live" row={row} />
          ))}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="py-6 space-y-3">
            <p className="text-sm font-medium text-foreground">
              All clear — no urgent calls
            </p>
            <p className="text-sm text-muted-foreground">
              {evaluationBacklogCount > 0
                ? `${evaluationBacklogCount} completed call${evaluationBacklogCount === 1 ? '' : 's'} still need evaluation.`
                : 'Open the live board when you are ready to monitor the feed.'}
            </p>
            {evaluationBacklogCount > 0 ? (
              <DashboardSectionAction
                onClick={() =>
                  navigateWithThreadPreservation(
                    navigate,
                    '/calls/evaluations',
                    searchParams
                  )
                }
              >
                Open evaluations queue
              </DashboardSectionAction>
            ) : (
              <DashboardSectionAction
                onClick={() =>
                  navigateWithThreadPreservation(
                    navigate,
                    '/calls/records',
                    searchParams
                  )
                }
              >
                Open live board
              </DashboardSectionAction>
            )}
          </CardContent>
        </Card>
      )}
    </section>
  )
}
